import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  CreditCard, Banknote, Loader2, CheckCircle, XCircle,
  Settings, ToggleLeft, ToggleRight, Shield, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

// PayPal icon component
const PayPalIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
  </svg>
);

const AdminPaymentMethodsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [methods, setMethods] = useState([]);
  const [expandedMethod, setExpandedMethod] = useState(null);
  
  // Form states for configuration
  const [paypalConfig, setPaypalConfig] = useState({
    client_id: '',
    client_secret: '',
    mode: 'sandbox',
    secret_set: false
  });
  
  const [stripeConfig, setStripeConfig] = useState({
    publishable_key: '',
    secret_key: '',
    mode: 'test',
    secret_key_set: false
  });

  useEffect(() => {
    const token = localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchMethods();
    fetchStripeConfig();
    fetchPayPalConfig();
  }, [navigate]);

  const fetchMethods = async () => {
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/payment-methods`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setMethods(data.methods);
      }
    } catch (error) {
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeConfig = async () => {
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/payment-methods/stripe/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.config) {
        setStripeConfig({
          publishable_key: data.config.publishable_key || '',
          secret_key: data.config.secret_key_set ? data.config.secret_key : '',
          mode: data.config.mode || 'test',
          secret_key_set: data.config.secret_key_set || false
        });
      }
    } catch (error) {
      console.error('Failed to load Stripe config:', error);
    }
  };

  const fetchPayPalConfig = async () => {
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/payment-methods/paypal/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.config) {
        setPaypalConfig({
          client_id: data.config.client_id || '',
          client_secret: data.config.secret_set ? data.config.client_secret : '',
          mode: data.config.mode || 'sandbox',
          secret_set: data.config.secret_set || false
        });
      }
    } catch (error) {
      console.error('Failed to load PayPal config:', error);
    }
  };

  const handleToggle = async (provider, currentStatus) => {
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/payment-methods/${provider}/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_enabled: !currentStatus })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMethods(prev => prev.map(m => 
          m.provider === provider ? { ...m, is_enabled: !currentStatus } : m
        ));
        toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} ${!currentStatus ? 'enabled' : 'disabled'}`);
      } else {
        toast.error('Failed to update payment method');
      }
    } catch (error) {
      toast.error('Failed to update payment method');
    }
  };

  const handleSaveConfig = async (provider) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      let config = provider === 'paypal' ? {...paypalConfig} : {...stripeConfig};
      
      // If secret key is empty and already set, don't send it (keep current)
      if (provider === 'stripe' && stripeConfig.secret_key_set && !stripeConfig.secret_key) {
        delete config.secret_key;
      }
      if (provider === 'paypal' && paypalConfig.secret_set && !paypalConfig.client_secret) {
        delete config.client_secret;
      }
      
      // Remove frontend-only fields
      delete config.secret_key_set;
      delete config.secret_set;
      
      const response = await fetch(`${API_BASE}/api/admin/payment-methods/${provider}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...config,
          is_enabled: methods.find(m => m.provider === provider)?.is_enabled || false
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Settings saved successfully');
        setExpandedMethod(null);
        fetchMethods();
        // Reload config to get updated state
        if (provider === 'stripe') fetchStripeConfig();
        if (provider === 'paypal') fetchPayPalConfig();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getIcon = (provider) => {
    switch (provider) {
      case 'cash':
        return <Banknote className="h-6 w-6" />;
      case 'paypal':
        return <PayPalIcon />;
      case 'stripe':
      case 'card':
        return <CreditCard className="h-6 w-6" />;
      default:
        return <CreditCard className="h-6 w-6" />;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
        <AdminSidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground" data-testid="payment-title">
              Payment Methods
            </h1>
            <p className="text-muted-foreground">
              Configure which payment methods are available to customers
            </p>
          </div>

          {/* Payment Methods List */}
          <div className="space-y-4">
            {methods.map((method) => (
              <Card 
                key={method.provider} 
                className={`bg-secondary border-border transition-all ${
                  method.is_enabled ? 'border-green-500/30' : ''
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${
                        method.is_enabled 
                          ? 'bg-green-500/10 text-green-500' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {getIcon(method.provider)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground flex items-center gap-2">
                          {method.name}
                          {method.is_enabled && (
                            <span className="text-xs bg-green-500/10 text-green-500 px-2 py-0.5 rounded">
                              Active
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground">{method.description}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {method.requires_config && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setExpandedMethod(
                            expandedMethod === method.provider ? null : method.provider
                          )}
                          data-testid={`configure-${method.provider}-btn`}
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Configure
                        </Button>
                      )}
                      <button
                        onClick={() => handleToggle(method.provider, method.is_enabled)}
                        className={`p-2 rounded-lg transition-colors ${
                          method.is_enabled 
                            ? 'text-green-500 hover:bg-green-500/10' 
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                        data-testid={`toggle-${method.provider}-btn`}
                      >
                        {method.is_enabled ? (
                          <ToggleRight className="h-8 w-8" />
                        ) : (
                          <ToggleLeft className="h-8 w-8" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Configuration */}
                  {expandedMethod === method.provider && method.requires_config && (
                    <div className="mt-6 pt-6 border-t border-border">
                      {method.provider === 'paypal' && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                            <Shield className="h-4 w-4" />
                            <span>Get your PayPal API credentials from</span>
                            <a 
                              href="https://developer.paypal.com/dashboard/applications" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary flex items-center gap-1 hover:underline"
                            >
                              PayPal Developer Dashboard
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-foreground block mb-2">
                              Client ID
                            </label>
                            <Input
                              value={paypalConfig.client_id}
                              onChange={(e) => setPaypalConfig(prev => ({ ...prev, client_id: e.target.value }))}
                              placeholder="Enter PayPal Client ID"
                              className="bg-background"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-foreground block mb-2">
                              Client Secret
                              {paypalConfig.secret_set && (
                                <span className="ml-2 text-xs text-green-500">(Currently set)</span>
                              )}
                            </label>
                            <Input
                              type="password"
                              value={paypalConfig.client_secret}
                              onChange={(e) => setPaypalConfig(prev => ({ ...prev, client_secret: e.target.value, secret_set: false }))}
                              placeholder={paypalConfig.secret_set ? "••••••••••••" : "Enter PayPal Client Secret"}
                              className="bg-background"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              {paypalConfig.secret_set 
                                ? "Leave empty to keep current secret, or enter new secret to update" 
                                : "Enter your PayPal Client Secret"}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-foreground block mb-2">
                              Mode
                            </label>
                            <select
                              value={paypalConfig.mode}
                              onChange={(e) => setPaypalConfig(prev => ({ ...prev, mode: e.target.value }))}
                              className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground"
                            >
                              <option value="sandbox">Sandbox (Testing)</option>
                              <option value="live">Live (Production)</option>
                            </select>
                            <p className="text-xs text-muted-foreground mt-1">
                              Use Sandbox for testing, Live for real payments
                            </p>
                          </div>
                          <Button onClick={() => handleSaveConfig('paypal')} disabled={saving}>
                            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Save PayPal Settings
                          </Button>
                        </div>
                      )}

                      {method.provider === 'stripe' && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                            <Shield className="h-4 w-4" />
                            <span>Get your Stripe API keys from</span>
                            <a 
                              href="https://dashboard.stripe.com/apikeys" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary flex items-center gap-1 hover:underline"
                            >
                              Stripe Dashboard
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-foreground block mb-2">
                              Publishable Key
                            </label>
                            <Input
                              value={stripeConfig.publishable_key}
                              onChange={(e) => setStripeConfig(prev => ({ ...prev, publishable_key: e.target.value }))}
                              placeholder="pk_test_... or pk_live_..."
                              className="bg-background"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-foreground block mb-2">
                              Secret Key
                              {stripeConfig.secret_key_set && (
                                <span className="ml-2 text-xs text-green-500">(Currently set)</span>
                              )}
                            </label>
                            <Input
                              type="password"
                              value={stripeConfig.secret_key}
                              onChange={(e) => setStripeConfig(prev => ({ ...prev, secret_key: e.target.value, secret_key_set: false }))}
                              placeholder={stripeConfig.secret_key_set ? "••••••••••••" : "sk_test_... or sk_live_..."}
                              className="bg-background"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              {stripeConfig.secret_key_set 
                                ? "Leave empty to keep current key, or enter new key to update" 
                                : "Enter your Stripe Secret Key"}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-foreground block mb-2">
                              Mode
                            </label>
                            <select
                              value={stripeConfig.mode}
                              onChange={(e) => setStripeConfig(prev => ({ ...prev, mode: e.target.value }))}
                              className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground"
                            >
                              <option value="test">Test Mode</option>
                              <option value="live">Live Mode</option>
                            </select>
                            <p className="text-xs text-muted-foreground mt-1">
                              Use Test Mode for development, Live Mode for real payments
                            </p>
                          </div>
                          <Button onClick={() => handleSaveConfig('stripe')} disabled={saving}>
                            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Save Stripe Settings
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Info Box */}
          <Card className="mt-8 bg-blue-500/5 border-blue-500/20">
            <CardContent className="p-6">
              <h3 className="font-semibold text-foreground flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-blue-500" />
                Payment Security
              </h3>
              <p className="text-sm text-muted-foreground">
                All online payments are processed securely through PayPal or Stripe. 
                Cash payments are collected by the driver upon arrival at the destination.
                No payment information is stored on our servers.
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminPaymentMethodsPage;
